import { toastSuccess } from '@/utils/toast'
import { ArrayHelpers, FormikConfig } from 'formik'
import { useSuspenseQuery } from '@tanstack/react-query'
import { templatesQueries, useUpdateRouteTemplate, RouteTemplate } from '@/features/templates'
import { systems } from '@knots/shared-frontend'
import { sentenceCase } from 'change-case'
import React from 'react'
import { Option } from '@/components/ui/Autocomplete/Autocomplete.types'

export type RouteTemplateSettingsSchemaType = Pick<
  RouteTemplate,
  'runtime' | 'type' | 'schedule' | 'scheduleTimeZone' | 'status' | 'authEnabled' | 'requirements'
>

export const useRouteTemplateForm = (
  templateId: number,
  close: () => void
): FormikConfig<RouteTemplateSettingsSchemaType> => {
  const {
    data: { template },
  } = useSuspenseQuery(templatesQueries.detail(templateId))

  const mutation = useUpdateRouteTemplate(templateId)

  const initialValues: RouteTemplateSettingsSchemaType = {
    runtime: template.runtime,
    type: template.type,
    schedule: template.schedule,
    scheduleTimeZone: template.scheduleTimeZone,
    status: 'draft',
    authEnabled: template.authEnabled,
    requirements: template.requirements ?? {
      connectors: [],
      secrets: [],
      params: [],
    },
  }

  const onSubmit = async (values: RouteTemplateSettingsSchemaType) => {
    const clearedValues: RouteTemplateSettingsSchemaType = {
      runtime: values.runtime,
      type: values.type,
      schedule: values.schedule ?? undefined,
      scheduleTimeZone: values.scheduleTimeZone ?? undefined,
      status: values.status ?? undefined,
      authEnabled: values.authEnabled ?? false,
      requirements: values.requirements,
    }

    await mutation.mutateAsync({ template: clearedValues })
    close()
    toastSuccess('Successfully updated the template')
  }

  return { initialValues, onSubmit }
}

export const curryAddNewConnector = <T extends any[]>(helpers: ArrayHelpers<T>) => {
  return () => helpers.push({ system: 'GENERIC', key: '', description: '' } as T[number])
}

export const curryAddNewSecret = <T extends any[]>(helpers: ArrayHelpers<T>) => {
  return () => helpers.push({ key: '', description: '' } as T[number])
}

export const curryAddNewParam = <T extends any[]>(helpers: ArrayHelpers<T>) => {
  return () => helpers.push({ key: '', description: '', type: 'string' } as T[number])
}

export const useSystemOptions = (): Option[] => {
  return React.useMemo(
    () =>
      systems.map((system) => ({
        label: sentenceCase(system),
        value: system,
      })),
    [systems]
  )
}
